import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { contactInfo as defaultContact, contactFaqs as defaultFaqs } from '@/mocks/contactData';
import { footerLinks, impactCounters as defaultCounters, ourPrograms as defaultPrograms, featuredCampaigns as defaultCampaigns } from '@/mocks/homeData';
import { galleryImages as defaultGalleryImages, galleryCategories as defaultGalleryCats } from '@/mocks/galleryData';
import { aboutTeam as defaultTeam } from '@/mocks/aboutData';
import type { GalleryImage } from '@/mocks/galleryData';

const STORAGE_KEY = 'hf_admin_data_v2';

export interface OrgInfo {
  name: string;
  tagline: string;
  description: string;
  logoUrl: string;
  regNo: string;
}

export interface ContactInfo {
  address: string;
  phone: string;
  email: string;
  helpline: string;
  hours: string;
  mapUrl: string;
}

export interface HeroSlide {
  url: string;
  alt: string;
  caption: string;
  tag: string;
}

export interface ImpactCounter {
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

export interface Program {
  name: string;
  desc: string;
  icon: string;
  color: string;
}

export interface SocialLink {
  name: string;
  icon: string;
  url: string;
}

export interface TeamMember {
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface Campaign {
  id: number;
  title: string;
  category: string;
  story: string;
  goal: number;
  raised: number;
  daysLeft: number;
  donors: number;
  imageUrl: string;
  urgency: string;
}

export interface SiteData {
  org: OrgInfo;
  contact: ContactInfo;
  heroSlides: HeroSlide[];
  impactCounters: ImpactCounter[];
  programs: Program[];
  galleryImages: GalleryImage[];
  galleryCats: typeof defaultGalleryCats;
  faqs: FaqItem[];
  socialLinks: SocialLink[];
  team: TeamMember[];
  campaigns: Campaign[];
}

const defaultHeroSlides: HeroSlide[] = [
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/03d2b155-bb18-4322-b5ea-e28ef04c548f_compressed_WhatsApp-Image-2026-07-28-at-1.03.10-PM.webp',
    alt: 'Happy Faces Hyderabad — girls community session',
    caption: 'Empowering Girls Across Hyderabad',
    tag: 'Education & Empowerment',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/3cce0a52-a2a7-4e74-930b-aa1a01e46561_compressed_WhatsApp-Image-2026-07-28-at-1.03.12-PM.webp',
    alt: 'Happy Faces Hyderabad — children group session',
    caption: 'Building Bright Futures Together',
    tag: 'Community Development',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/ffdcfd54-4b00-4116-a2e7-12ec73716629_compressed_WhatsApp-Image-2026-07-28-at-1.03.13-PM.webp',
    alt: 'Happy Faces Hyderabad — official banner event',
    caption: 'Registered & Trusted NGO Since 2026',
    tag: 'Reg. No: 4.3 of 2026',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/1c7a5be3-d015-4be9-bb55-83c43dd932f3_compressed_WhatsApp-Image-2026-07-28-at-1.03.16-PM.webp',
    alt: 'Happy Faces Hyderabad — children community event',
    caption: 'Spreading Smiles Every Day',
    tag: 'Food & Blanket Distribution',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/fcc529ed-7139-48bb-9cb9-20d7d39555df_compressed_WhatsApp-Image-2026-07-28-at-1.03.18-PM.webp',
    alt: 'Happy Faces Hyderabad — boys school outreach',
    caption: 'Inspiring the Next Generation',
    tag: 'Youth Outreach',
  },
];

const defaultSiteData: SiteData = {
  org: {
    name: 'Happy Faces Hyderabad',
    tagline: 'Rural Development Welfare Society',
    description: 'A verified NGO dedicated to transforming lives through food distribution, blanket distribution, education, healthcare, women empowerment, and community development across Hyderabad & rural India.',
    logoUrl: 'https://static.readdy.ai/image/5b0394d63ea67e242d80d6c69b47f9d5/248fb4068954f3ccbfda9e34faec5a86.png',
    regNo: '4.3 of 2026',
  },
  contact: {
    address: defaultContact.address,
    phone: defaultContact.phone,
    email: defaultContact.email,
    helpline: defaultContact.helpline,
    hours: defaultContact.hours,
    mapUrl: 'https://maps.google.com/maps?q=17.40445327758789,78.50751495361328&z=17&output=embed',
  },
  heroSlides: defaultHeroSlides,
  impactCounters: defaultCounters,
  programs: defaultPrograms,
  galleryImages: defaultGalleryImages,
  galleryCats: defaultGalleryCats,
  faqs: defaultFaqs,
  socialLinks: footerLinks.socialLinks,
  team: defaultTeam.map((m) => ({
    name: m.name,
    role: m.role,
    bio: m.bio,
    imageUrl: m.imageUrl,
  })),
  campaigns: defaultCampaigns,
};

interface SiteDataContextValue {
  data: SiteData;
  updateOrg: (org: OrgInfo) => void;
  updateContact: (contact: ContactInfo) => void;
  updateHeroSlides: (slides: HeroSlide[]) => void;
  updateImpactCounters: (counters: ImpactCounter[]) => void;
  updatePrograms: (programs: Program[]) => void;
  updateGalleryImages: (images: GalleryImage[]) => void;
  updateFaqs: (faqs: FaqItem[]) => void;
  updateSocialLinks: (links: SocialLink[]) => void;
  updateTeam: (team: TeamMember[]) => void;
  updateCampaigns: (campaigns: Campaign[]) => void;
  resetAll: () => void;
}

const SiteDataContext = createContext<SiteDataContextValue | null>(null);

function loadFromStorage(): SiteData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSiteData;
    const parsed = JSON.parse(raw) as Partial<SiteData>;
    return { ...defaultSiteData, ...parsed };
  } catch {
    return defaultSiteData;
  }
}

export function SiteDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<SiteData>(loadFromStorage);

  const save = useCallback((newData: SiteData) => {
    setData(newData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
  }, []);

  const updateOrg = useCallback((org: OrgInfo) => save({ ...data, org }), [data, save]);
  const updateContact = useCallback((contact: ContactInfo) => save({ ...data, contact }), [data, save]);
  const updateHeroSlides = useCallback((heroSlides: HeroSlide[]) => save({ ...data, heroSlides }), [data, save]);
  const updateImpactCounters = useCallback((impactCounters: ImpactCounter[]) => save({ ...data, impactCounters }), [data, save]);
  const updatePrograms = useCallback((programs: Program[]) => save({ ...data, programs }), [data, save]);
  const updateGalleryImages = useCallback((galleryImages: GalleryImage[]) => save({ ...data, galleryImages }), [data, save]);
  const updateFaqs = useCallback((faqs: FaqItem[]) => save({ ...data, faqs }), [data, save]);
  const updateSocialLinks = useCallback((socialLinks: SocialLink[]) => save({ ...data, socialLinks }), [data, save]);
  const updateTeam = useCallback((team: TeamMember[]) => save({ ...data, team }), [data, save]);
  const updateCampaigns = useCallback((campaigns: Campaign[]) => save({ ...data, campaigns }), [data, save]);
  const resetAll = useCallback(() => { localStorage.removeItem(STORAGE_KEY); save(defaultSiteData); }, [save]);

  useEffect(() => {
    // Sync if localStorage changes in another tab
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setData(loadFromStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <SiteDataContext.Provider value={{
      data,
      updateOrg,
      updateContact,
      updateHeroSlides,
      updateImpactCounters,
      updatePrograms,
      updateGalleryImages,
      updateFaqs,
      updateSocialLinks,
      updateTeam,
      updateCampaigns,
      resetAll,
    }}>
      {children}
    </SiteDataContext.Provider>
  );
}

export function useSiteData() {
  const ctx = useContext(SiteDataContext);
  if (!ctx) throw new Error('useSiteData must be used within SiteDataProvider');
  return ctx;
}